import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ScrollText, Quote, Flame } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Soins", "Protection", "Exorcisme", "Aide", "Wicca"];

const Temoignages = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", city: "", category: "Soins", text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get("/testimonies");
      setItems(data);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  useEffect(() => {
    if (user?.name) setForm((f) => ({ ...f, name: user.name }));
  }, [user]);

  const submitTestimony = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/testimonies", form);
      toast.success("Merci — votre témoignage sera publié après relecture.");
      setForm({ name: user.name || "", city: "", category: "Soins", text: "" });
      setShowForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20" data-testid="temoignages-page">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-6">
        <div>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--ivory)] leading-tight">
            <em className="text-[var(--gold)]">Témoignages</em>
          </h1>
          <p className="font-serif-body text-lg text-[var(--ivory-muted)] max-w-2xl mt-4">
            N'hésitez pas à témoigner ou à nous faire partager vos expériences.
          </p>
        </div>

        {/* Auth-aware CTA */}
        {user?.is_donor ? (
          <button
            onClick={() => setShowForm((s) => !s)}
            className="btn-sacred btn-sacred-filled sharp"
            data-testid="toggle-testimony-form"
          >
            {showForm ? "Fermer le formulaire" : "Partager mon témoignage"}
          </button>
        ) : user ? (
          <Link to="/abonnement" className="btn-sacred sharp" data-testid="cta-subscribe">
            Devenir gardien pour témoigner
          </Link>
        ) : (
          <Link to="/connexion" className="btn-sacred sharp" data-testid="cta-login">
            Se connecter pour témoigner
          </Link>
        )}
      </div>

      {/* Testimony form (donors only, toggled) */}
      {user?.is_donor && showForm && (
        <div className="mb-16 mt-8 sacred-card sharp p-10 border-t-2 border-[var(--gold)]/40" data-testid="testimony-form-wrapper">
          <div className="flex items-center gap-3 mb-6">
            <Quote className="text-[var(--gold)]" strokeWidth={1.1} size={28} />
            <h2 className="font-serif-display text-3xl text-[var(--ivory)]">Partager un témoignage</h2>
          </div>
          <p className="font-serif-body text-[var(--ivory-muted)] mb-6 leading-relaxed">
            Votre parole encourage d'autres âmes en chemin. Après relecture, votre témoignage
            sera publié ici.
          </p>
          <form onSubmit={submitTestimony} data-testid="testimony-form">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="font-engraved text-[10px] text-[var(--gold)] block mb-2">Nom (ou initiale)</label>
                <input required minLength={2} maxLength={80} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="sacred-input sharp" placeholder="Marie L., Étienne D…" data-testid="testimony-name" />
              </div>
              <div>
                <label className="font-engraved text-[10px] text-[var(--gold)] block mb-2">Ville</label>
                <input required minLength={2} maxLength={80} value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="sacred-input sharp" placeholder="Lyon, Nantes…" data-testid="testimony-city" />
              </div>
            </div>
            <div className="mb-4">
              <label className="font-engraved text-[10px] text-[var(--gold)] block mb-2">Domaine</label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-px bg-[rgba(212,175,55,0.15)]">
                {CATEGORIES.map((c) => (
                  <button type="button" key={c}
                    onClick={() => setForm({ ...form, category: c })}
                    className={`py-3 font-engraved text-[10px] transition ${form.category === c ? "bg-[var(--bordeaux)] text-[var(--ivory)]" : "bg-[rgba(17,19,26,0.8)] text-[var(--ivory-muted)] hover:text-[var(--gold)]"}`}
                    data-testid={`testimony-cat-${c.toLowerCase()}`}>{c}</button>
                ))}
              </div>
            </div>
            <div className="mb-6">
              <label className="font-engraved text-[10px] text-[var(--gold)] block mb-2">Votre témoignage (20 à 1500 caractères)</label>
              <textarea required minLength={20} maxLength={1500} rows={7} value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                className="sacred-input sharp resize-none"
                placeholder="Écrivez librement ce que le sanctuaire vous a apporté…"
                data-testid="testimony-text" />
            </div>
            <button type="submit" disabled={submitting}
              className="btn-sacred btn-sacred-filled sharp w-full md:w-auto" data-testid="testimony-submit">
              {submitting ? "Envoi…" : "Envoyer mon témoignage"}
            </button>
          </form>
        </div>
      )}

      {/* Divider */}
      <div className="divider-ornament my-10"><Flame size={14} strokeWidth={1.2} /></div>

      {/* Public list */}
      {items.length === 0 ? (
        <div className="sacred-card sharp p-16 text-center" data-testid="temoignages-empty">
          <ScrollText className="text-[var(--gold)] mx-auto mb-6" strokeWidth={1.1} size={32} />
          <p className="font-serif-display text-2xl text-[var(--ivory)] italic">
            Les premiers témoignages seront bientôt recueillis.
          </p>
          <p className="font-serif-body text-[var(--ivory-muted)] mt-4">
            Cet espace reste ouvert au silence avant que les voix s'y déposent.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-px bg-[rgba(212,175,55,0.12)]">
          {items.map((t) => (
            <div key={t.id} className="sacred-card sharp p-10" data-testid={`temoignage-${t.id}`}>
              <ScrollText className="text-[var(--gold)] mb-4" strokeWidth={1.1} size={22} />
              <p className="font-serif-display text-xl md:text-2xl italic text-[var(--ivory)] leading-snug mb-6">
                « {t.text} »
              </p>
              <div className="flex justify-between items-end">
                <p className="font-engraved text-[10px] text-[var(--gold)]">{t.name} — {t.city}</p>
                <p className="font-engraved text-[10px] text-[var(--ivory-muted)]">{t.category}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Temoignages;
